<script>
	import queryString from "query-string";
	import { onDestroy, onMount } from "svelte";
	import { navigate } from "svelte-routing";
	import { get } from "svelte/store";
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import Swal from "sweetalert2";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import FilterForm from "../../../components/FilterForm/FilterForm.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import SizeSelect from "../../../components/SizeSelect/SizeSelect.svelte";
	import client from "../../../services/client";
	import { listQueryParams } from "../../../services/helpers";
	import {
		errorMessage,
		initPage,
		loading,
		sizeSelect,
		totalPages,
	} from "../../../store/store";
	import ManageTable from "./ManageTable.svelte";

	let data = [];
	let files;

	const query = queryString.parse(window.location.search);
	onMount(async () => {
		try {
			loading.set(true);
			const res = await client("/v0/Auth/GetUsersWithFilter", {
				method: "POST",
				version: "Tasking",
				data: { ...listQueryParams(query), limit: get(sizeSelect) },
			});
			data = res.data.data.users;
			totalPages.set(res.data.data.totalPages);
			loading.set(false);
		} catch (err) {
			loading.set(false);
			console.log(err);
		}
	});
	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	onDestroy(() => {
		initPage.set(1);
		sizeSelect.set("20");
	});

	//function to call jobs api on select limit size
	async function trigerSelectUser(limit) {
		try {
			loading.set(true);
			const res = await client("/v0/Auth/GetUsersWithFilter", {
				method: "POST",
				version: "Tasking",
				data: { ...listQueryParams(query), limit: limit },
			});
			data = res.data.data.users;
			totalPages.set(res.data.data.totalPages);
			loading.set(false);
		} catch (err) {
			loading.set(false);
			console.log(err);
		}
	}

	async function handleUserStatus(userId, status) {
		try {
			loading.set(true);
			const res = await client("/v0/Auth/UpdateStatus", {
				method: "POST",
				version: "Tasking",
				data: {
					userId,
					status,
				},
			});
			if (res.data.error_code === 0) {
				trigerSelectUser();
			}
		} catch (err) {
			console.log(err.response);
		}
	}

	//function to handle import CSV
	async function handleImport(e) {
		Swal.fire({
			title: `Do you want to upload this file <b class="link-text mt-2 fs-3">${e.target.files[0].name}</b>?`,
			showDenyButton: true,
			confirmButtonText: "<b>OK</b>",
			denyButtonText: `Don't upload`,
			confirmButtonColor: "#19F9B9",
			customClass: {
				confirmButton: "primary", //insert class here
			},
		}).then((result) => {
			/* Read more about isConfirmed, isDenied below */
			if (result.isConfirmed) {
				const dataArray = new FormData();
				dataArray.append("File", e.target.files[0]);
				client("/v0/Auth/Import", {
					method: "POST",
					version: "Tasking",
					header: "multipart",
					data: dataArray,
				})
					.then((res) => {
						if (res.data.error_code === 400) {
							const rejects = res.data.data.rejects
								.map((reject) => {
									return (
										"- " +
										reject.errorMessage +
										" at row " +
										"<b>" +
										reject.lineNumber +
										"</b>"
									);
								})
								.join("<br>");
							Swal.fire({
								icon: "error",
								title: "Oops...",
								html: `<p>${rejects}</P> <br> <br> <p class="fs-4"><b>Please check your file and try again!</b></p>`,
								confirmButtonColor: "#DC3741",
							});

							files = "";
							e.target.value = "";
						} else if (res.data.error_code === 0) {
							Swal.fire({
								icon: "success",
								title: "Success!",
								text: ` Jobs were imported`,
							});
							files = "";
							e.target.value = "";
						}
					})
					.catch((err) => {
						Swal.fire({
							icon: "error",
							title: "Oops...",
							text: "Something went wrong!",
						});
						files = "";
						e.target.value = "";
					});
			} else if (result.isDenied) {
				Swal.fire({
					text: "Changes are not saved",
					icon: "info",
					confirmButtonColor: "#19F9B9",
					customClass: {
						confirmButton: "primary", //insert class here
					},
				});
				files = "";
				e.target.value = "";
			}
		});
	}

	//function to download csv template
	const handleDownloadCSV = async () => {
		try {
			const res = await client("/v0/Auth/DownloadTemplate", {
				method: "GET",
				version: "Tasking",
				header: "csv",
			});

			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "UserTemplate.csv"); //or any other extension
			document.body.appendChild(link);
			link.click();

			// ;
		} catch (err) {
			console.log(err);
		}
	};

	//function to handle export to csv
	export const handleExportCSV = async (status) => {
		try {
			const res = await client("/v0/Auth/Export", {
				method: "GET",
				version: "Tasking",
				header: "csv",
			});

			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "UserList.csv"); //or any other extension
			document.body.appendChild(link);
			link.click();

			// ;
		} catch (err) {
			console.log(err);
		}
	};
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={err} />
			<Col className="row d-flex align-items-start mx-0">
				<PageHeader title="Account Management" />
			</Col>
			<div class="d-flex flex-row">
				<Col xs={6}>
					<FilterForm name mobileNumber email status userStatus />
				</Col>
				<Col class="d-flex justify-content-end" xs={6}>
					<div class="mx-2">
						<p class="link-text mt-2 fs-4">
							{!files || (files && files.length < 1)
								? ""
								: files[0].name}
						</p>
					</div>
					<div class="mx-2">
						<input
							id="fileInput"
							type="file"
							multiple={false}
							hidden="hidden"
							bind:files
							on:change={(e) => handleImport(e)}
						/>
						<label
							type="button"
							for="fileInput"
							id="customButton"
							class="btn themeButton primary px-4"
							block>Import CSV</label
						>
						<p>
							<a
								href="/assets/csv/User_Template.csv"
								target="_blank"
								download="user_template.csv"
								rel="noreferrer"
								class="link link-text"
							>
								Download CSV Template
							</a>
						</p>
					</div>
					<div class="mx-2">
						<Button
							on:click={() => handleExportCSV(0)}
							class="themeButton primary px-4">Export CSV</Button
						>
					</div>
					<div class="mx-2">
						<Button
							class="themeButton primary px-4"
							on:click={() =>
								navigate("/account/account-management/create")}
							>CREATE USER</Button
						>
						<div class="my-2">
							<SizeSelect {trigerSelectUser} />
						</div>
					</div>
				</Col>
			</div>
			<div />
		</div>
	</Col>
	<Col class="my-2">
		<ManageTable {data} {handleUserStatus} />
	</Col>
</Row>
